import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import SalvareDate from "../../SalvareDate";
import {
  getStorage,
  ref,
  listAll,
  getDownloadURL,
  deleteObject,
  uploadBytes,
} from "firebase/storage";
import "../../stiluri/DosarJuridic.css";

const DosarMedical = ({ beneficiar, setBeneficiar, setEditMode }) => {
  const { id } = useParams();
  const numedosar = "DosarMedical";


  // === Firebase Storage logic ===
  const storage = getStorage();
  const rootPath = `${id}/dosar_medical/`;

  const [tree, setTree] = useState(null);
  const [expandedPaths, setExpandedPaths] = useState(new Set([rootPath]));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(rootPath);

  const loadFolderStructure = useCallback(async (path) => {
    try {
      const folderRef = ref(storage, path);
      const res = await listAll(folderRef);

      const folders = await Promise.all(
        res.prefixes.map((folderRef) => loadFolderStructure(folderRef.fullPath))
      );

      const files = res.items.map((itemRef) => ({
        name: itemRef.name,
        fullPath: itemRef.fullPath,
        url: null,
      }));

      return {
        name: path.split("/").filter(Boolean).pop() || "root",
        path,
        folders,
        files,
      };
    } catch (err) {
      console.error(err);
      return null;
    }
  }, [storage]);

  const loadFilesForNode = async (node) => {
    const folderRef = ref(storage, node.path);
    const res = await listAll(folderRef);
    const files = await Promise.all(
      res.items.map(async (itemRef) => ({
        name: itemRef.name,
        fullPath: itemRef.fullPath,
        url: null,
      }))
    );
    return files;
  };

  const expandNode = async (path, currentNode) => {
    setExpandedPaths((prev) => new Set(prev).add(path));

    if (currentNode && currentNode.files.length === 0) {
      const files = await loadFilesForNode(currentNode);
      const updateTree = (node) => {
        if (node.path === path) {
          return { ...node, files };
        }
        return {
          ...node,
          folders: node.folders.map(updateTree),
        };
      };
      setTree((prevTree) => updateTree(prevTree));
    }
  };

  const toggleExpand = async (path, node) => {
    if (expandedPaths.has(path)) {
      setExpandedPaths((prev) => {
        const newSet = new Set(prev);
        newSet.delete(path);
        return newSet;
      });
    } else {
      await expandNode(path, node);
    }
  };

  const handlePreview = async (file) => {
    if (!file.url) {
      const url = await getDownloadURL(ref(storage, file.fullPath));
      setPreview({ ...file, url });
    } else {
      setPreview(file);
    }
  };

  const handleDeleteFile = async (file) => {
    if (window.confirm(`Ștergi fișierul "${file.name}"?`)) {
      try {
        const fileRef = ref(storage, file.fullPath);
        await deleteObject(fileRef);
        alert("Fișier șters cu succes!");
        await expandNode(file.fullPath.substring(0, file.fullPath.lastIndexOf('/')), tree);
        setPreview(null);
      } catch (err) {
        alert("Eroare la ștergerea fișierului.");
        console.error(err);
      }
    }
  };

  const getAllFolders = (node) => {
    if (!node) return [];
    let foldersList = [{ name: node.name, path: node.path }];
    node.folders.forEach((folder) => {
      foldersList = foldersList.concat(getAllFolders(folder));
    });
    return foldersList;
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      alert("Alege cel puțin un fișier!");
      return;
    }
    setUploading(true);
    try {
      const pathWithSlash = selectedFolder.endsWith('/') ? selectedFolder : selectedFolder + '/';
      await Promise.all(
        selectedFiles.map((file) => {
          const fileRef = ref(storage, pathWithSlash + file.name);
          return uploadBytes(fileRef, file);
        })
      );
      alert("Încărcare reușită!");
      await expandNode(selectedFolder, tree);
      setSelectedFiles([]);
      setShowUploadModal(false);
    } catch (err) {
      alert("Eroare la upload.");
      console.error(err);
    }
    setUploading(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
    }
  };

  const renderNode = (node) => {
    if (!node) return null;
    const isExpanded = expandedPaths.has(node.path);
    return (
      <div className="folder" key={node.path}>
        <div
          className="folder-name"
          onClick={() => toggleExpand(node.path, node)}
          role="button"
          tabIndex={0}
        >
          {isExpanded ? "📂" : "📁"} {node.name}
        </div>
        {isExpanded && (
          <div className="folder-contents">
            {node.folders.map((folder) => renderNode(folder))}
            {node.files.map((file) => (
              <div className="file" key={file.fullPath}>
                <span
                  className="file-name"
                  onClick={() => handlePreview(file)}
                  role="button"
                  tabIndex={0}
                >
                  📄 {file.name}
                </span>
                <button className="preview-btn" onClick={() => handlePreview(file)}>👁️</button>
                <a
                  href={file.url || "#"}
                  onClick={(e) => { if (!file.url) e.preventDefault(); }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-link"
                >⬇️</a>
                <button className="delete-btn" onClick={() => handleDeleteFile(file)}>❌</button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!id) {
      setError("ID-ul beneficiarului nu este definit.");
      return;
    }
    setLoading(true);
    loadFolderStructure(rootPath)
      .then((data) => {
        setTree(data);
        setSelectedFolder(rootPath);
        setLoading(false);
        setError(null);
      })
      .catch(() => {
        setError("Eroare la încărcarea dosarului.");
        setLoading(false);
      });
  }, [id, loadFolderStructure, rootPath]);

  return (
    <div className="detalii-grid">
      <h2>Dosar Medical</h2>

      <label><strong>Diagnostic medical:</strong></label>
      <input
        type="text"
        value={beneficiar.diagnostic_medical || ""}
        onChange={(e) => setBeneficiar({ ...beneficiar, diagnostic_medical: e.target.value })}
      />

      <label><strong>Status HIV:</strong></label>
      <select
        value={beneficiar.statusHIV || ""}
        onChange={(e) => setBeneficiar({ ...beneficiar, statusHIV: e.target.value })}
      >
        <option value="">Selectează un răspuns</option>
        <option value="Pozitiv">Pozitiv</option>
        <option value="Negativ">Negativ</option>
        <option value="Necunoscut">Necunoscut</option>
      </select>

      <label><strong>Status TBC:</strong></label>
      <select
        value={beneficiar.status_tbc || ""}
        onChange={(e) => setBeneficiar({ ...beneficiar, status_tbc: e.target.value })}
      >
        <option value="">Selectează un răspuns</option>
        <option value="Pozitiv">Pozitiv</option>
        <option value="Negativ">Negativ</option>
        <option value="Necunoscut">Necunoscut</option>
      </select>

      <label><strong>Consum alcool:</strong></label>
      <select
        value={beneficiar.consum_alcool || ""}
        onChange={(e) => setBeneficiar({ ...beneficiar, consum_alcool: e.target.value })}
      >
        <option value="">Selectează un răspuns</option>
        <option value="Frecvent">Frecvent</option>
        <option value="Ocazional">Ocazional</option>
        <option value="Nu">Nu</option>
        <option value="Fost consumator">Fost consumator</option>
      </select>

      <label><strong>Consum droguri:</strong></label>
      <select
        value={beneficiar.consum_droguri || ""}
        onChange={(e) => setBeneficiar({ ...beneficiar, consum_droguri: e.target.value })}
      >
        <option value="">Selectează un răspuns</option>
        <option value="Frecvent">Frecvent</option>
        <option value="Ocazional">Ocazional</option>
        <option value="Nu">Nu</option>
        <option value="Fost consumator">Fost consumator</option>
      </select>

      <label><strong>Stare autonomie:</strong></label>
      <select
        value={beneficiar.stare_autonomie || ""}
        onChange={(e) => setBeneficiar({ ...beneficiar, stare_autonomie: e.target.value })}
      >
        <option value="">Selectează un răspuns</option>
        <option value="Autonom">Autonom</option>
        <option value="Partial autonom">Parțial autonom</option>
        <option value="Dependent">Dependent</option>
      </select>

      <SalvareDate beneficiar={beneficiar} id={id} setEditMode={setEditMode} numedosar={numedosar} />

      

      <h3>Fișiere Dosar Medical</h3>
      {loading && <p>Se încarcă dosarul medical...</p>}
      {error && <p className="error-msg">{error}</p>}
      {tree ? renderNode(tree) : <p>Te rugăm să aștepți.</p>}

      {preview && (
        <div className="preview-overlay" onClick={() => setPreview(null)}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setPreview(null)}>×</button>
            <h3>{preview.name}</h3>
            {preview.url ? (
              preview.name.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                <img src={preview.url} alt={preview.name} className="preview-image" />
              ) : preview.name.match(/\.pdf$/i) ? (
                <iframe src={preview.url} title={preview.name} className="preview-pdf" />
              ) : (
                <p>Preview indisponibil.</p>
              )
            ) : (
              <p>Se încarcă...</p>
            )}
          </div>
        </div>
      )}

      <button
        className="upload-btn"
        onClick={() => setShowUploadModal(true)}
        style={{ marginTop: "1rem",   maxWidth: "150px" }}
      >
        Încarcă fișier
      </button>


      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)} onDragOver={handleDragOver} onDrop={handleDrop}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} onDragOver={handleDragOver} onDrop={handleDrop}>
            <button className="close-btn" onClick={() => setShowUploadModal(false)}>×</button>
            <h3>Încarcă fișier</h3>
            <p>Trage fișierele aici sau folosește selectorul de fișiere.</p>
            <form onSubmit={handleUploadSubmit}>
              <label htmlFor="folderSelect">Folder:</label>
              <select id="folderSelect" value={selectedFolder} onChange={(e) => setSelectedFolder(e.target.value)}>
                {tree && getAllFolders(tree).map((folder) => (
                  <option key={folder.path} value={folder.path}>{folder.path}</option>
                ))}
              </select>
              <input
                type="file"
                multiple
                onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                required={selectedFiles.length === 0}
                disabled={uploading}
                style={{ marginTop: "1rem" }}
              />
              {selectedFiles.length > 0 && <p>{selectedFiles.length} fișier(e) selectat(e)</p>}
              <button type="submit" disabled={uploading}>{uploading ? "Se încarcă..." : "Încarcă"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DosarMedical;